import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { confirmDialog } from "../lib/confirm";
import TodayArrivalWarnings from "../components/TodayArrivalWarnings";
import StatusCleanupCard from "../components/StatusCleanupCard";
import QuoteDecisionHistoryCard from "../components/QuoteDecisionHistoryCard";
import OpenTodosCard from "../components/OpenTodosCard";
import PropertiesWithoutConciergeCard from "../components/PropertiesWithoutConciergeCard";
import RevenueChart from "../components/RevenueChart";
import ConciergeStatsCard from "../components/ConciergeStatsCard";
import NextArrivalsRadar from "../components/NextArrivalsRadar";
import ConciergeHome from "./ConciergeHome";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";

function TelegramPushCard({ user }) {
  const [chatId, setChatId] = useState(user?.telegram_chat_id || "");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/me/preferences", { telegram_chat_id: chatId.trim() || null });
      toast.success(chatId ? "Chat-ID gespeichert." : "Chat-ID entfernt.");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      await api.post("/me/telegram/test");
      toast.success("Test-Push gesendet — bitte Telegram öffnen.");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Fehler beim Senden."); }
    finally { setTesting(false); }
  };

  return (
    <div className="bg-white border border-navy/10 p-6 md:p-7" data-testid="telegram-push-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="label-caps mb-2">Push-Benachrichtigungen · Telegram</div>
          <h2 className="font-display text-2xl text-navy">Dringende Ankünfte</h2>
          <p className="text-[13px] text-navy-dark/70 mt-2 max-w-xl">
            Sie erhalten sofort eine Telegram-Nachricht, wenn ein Kunde eine Ankunft innerhalb der nächsten 48 Stunden anfragt.
            So können Sie die Vorbereitung ohne Verzögerung planen.
          </p>
        </div>
        {user?.telegram_chat_id && (
          <span className="inline-flex items-center gap-2 px-3 py-1 text-[10.5px] tracking-[0.2em] uppercase border" style={{ borderColor: "#2E5D4E40", color: BRAND_COLORS.greenDeep }}>
            <span className="w-1.5 h-1.5" style={{ background: BRAND_COLORS.greenDeep }} />Aktiv
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3">
        <input
          data-testid="telegram-chatid-input"
          value={chatId}
          onChange={(e) => setChatId(e.target.value)}
          placeholder="Ihre Telegram-Chat-ID (z. B. 959862760)"
          className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-[14px]"
        />
        <button
          data-testid="telegram-save-btn"
          onClick={save}
          disabled={saving}
          className="text-ivory px-5 py-3 text-[11px] tracking-[0.15em] uppercase hover:opacity-90 disabled:opacity-40"
          style={{ background: BRAND_COLORS.navy }}
        >
          {saving ? "…" : "Speichern"}
        </button>
        <button
          data-testid="telegram-test-btn"
          onClick={test}
          disabled={testing || !user?.telegram_chat_id}
          className="border border-gold text-navy px-5 py-3 text-[11px] tracking-[0.15em] uppercase hover:bg-gold/10 disabled:opacity-40"
        >
          {testing ? "Sende…" : "Test-Nachricht"}
        </button>
      </div>
      <details className="mt-4 text-[12px] text-navy-dark/70">
        <summary className="cursor-pointer hover:text-navy">So finden Sie Ihre Chat-ID (2 Min.)</summary>
        <ol className="mt-3 pl-5 space-y-1 list-decimal">
          <li>Öffnen Sie <a href="https://t.me/Weissblom_concierge_bot" target="_blank" rel="noreferrer" className="text-navy border-b border-gold">@Weissblom_concierge_bot</a> in Telegram</li>
          <li>Klicken Sie <strong>START</strong> und schreiben Sie irgendetwas</li>
          <li>Öffnen Sie im Browser <code className="bg-navy/5 px-1">api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code></li>
          <li>Kopieren Sie den <code className="bg-navy/5 px-1">chat.id</code> Wert und speichern Sie ihn hier</li>
        </ol>
      </details>
    </div>
  );
}

function Stat({ label, value, testId, to }) {
  // Feb 2026: Statistik-Kacheln sind interaktiv — ein Klick navigiert direkt
  // in das passende Untermenü (Kundenverwaltung · Objekte · Kalender ·
  // Anfragen · Chat). Ohne `to`-Prop bleibt die Kachel statisch.
  const shell = (
    <>
      <div className="label-caps mb-3">{label}</div>
      <div className="font-display text-4xl md:text-5xl text-navy">{value}</div>
    </>
  );
  const base = "bg-white border border-navy/10 p-6";
  if (!to) {
    return (
      <div className={base} data-testid={testId}>
        {shell}
      </div>
    );
  }
  return (
    <Link
      to={to}
      className={`${base} block transition-colors hover:border-gold focus:outline-none focus:border-gold cursor-pointer`}
      data-testid={testId}
      title="Details öffnen →"
    >
      {shell}
    </Link>
  );
}

export default function TeamDashboard() {
  const { t } = useI18n();
  const { user, effectiveRole, activeConciergeId, activeConciergeName } = useAuth();
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [pending, setPending] = useState(null);
  const [draftCount, setDraftCount] = useState(null);
  const [showTour, setShowTour] = useState(false);
  // "Offene To-dos"-summary from OpenTodosCard — { customer_ids_with_open,
  // request_ids_open, ... }. The card is authoritative for filter targets.
  const [openSummary, setOpenSummary] = useState(null);
  const [searchParams] = useSearchParams();
  const filterOpenOnly = searchParams.get("open") === "1";

  // When the filter is active, restrict the two lists on the dashboard to
  // entries that actually have work still open. The card also links to the
  // calendar with the same query param preserved.
  const filteredCustomers = useMemo(() => {
    if (!filterOpenOnly || !openSummary) return customers;
    const set = new Set(openSummary.customer_ids_with_open || []);
    return customers.filter((c) => set.has(c.id));
  }, [customers, filterOpenOnly, openSummary]);
  const filteredRequests = useMemo(() => {
    if (!filterOpenOnly || !openSummary) return requests;
    const set = new Set(openSummary.request_ids_open || []);
    return requests.filter((r) => set.has(r.id));
  }, [requests, filterOpenOnly, openSummary]);

  const userId = user?.id;
  const userRole = user?.role;
  const userOnboarded = user?.onboarded_at;
  useEffect(() => {
    (async () => {
      try {
        // Feb 2026 · Concierge-Impersonation-Kaskade: wenn ein Admin
        // gerade als Concierge unterwegs ist, klammern wir alle Cockpit-
        // Zahlen auf den gewählten Concierge (`?scope_concierge_id=...`).
        // Echte Concierges brauchen keinen Query-Param — Backend leitet
        // ihre eigene id aus dem JWT ab.
        const statsUrl = activeConciergeId && userRole === "admin"
          ? `/team/stats?scope_concierge_id=${encodeURIComponent(activeConciergeId)}`
          : "/team/stats";
        const [s, c, r, pu, dr] = await Promise.all([
          api.get(statsUrl),
          api.get("/team/customers"),
          api.get("/requests"),
          api.get("/team/pending-updates"),
          api.get("/team/weekly-drafts").catch(() => ({ data: [] })),
        ]);
        setStats(s.data);
        setCustomers(c.data);
        setRequests(r.data);
        setPending(pu.data);
        setDraftCount(Array.isArray(dr.data) ? dr.data.length : 0);
        if (userId && !userOnboarded) setShowTour(true);
      } catch (e) {
        console.error("TeamDashboard load failed:", e);
      }
    })();
  }, [userId, userRole, userOnboarded, activeConciergeId]);

  const finishTour = async () => {
    try { await api.post("/me/onboarded"); } catch (e) { console.error("onboarded ping failed:", e); }
    setShowTour(false);
  };

  // Feb 2026 · Concierge-Ansicht drastisch vereinfacht (User-Directive):
  //   „concierge Ebene: Nur Kalender · Objekte · Profil und seine
  //    zugewiesene Listen wie schon installiert."
  // Wenn effectiveRole=concierge (echter Concierge ODER Admin-Impersonation),
  // rendern wir stattdessen den ConciergeHome-Startbildschirm — Anreise-
  // Radar, Objekte-Übersicht, Direktlinks. WICHTIG: Der Early-Return
  // steht NACH allen Hooks, damit die Hooks-Reihenfolge stabil bleibt.
  if (effectiveRole === "concierge") {
    return <ConciergeHome />;
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-16" data-testid="team-dashboard">
      {/* Prominent red banner surfaced ABOVE everything else when arrivals
          are scheduled for today but still have open checklist items — so
          nobody misses a hand-over that's a few hours away. */}
      <TodayArrivalWarnings />
      {/* Feb 2026 · Warn-Kachel „Objekte ohne Concierge" — rendert sich
          nur wenn tatsächlich Lücken existieren. Steht ganz oben, damit
          jedem Admin beim Login sofort auffällt, wo Zuweisungen fehlen. */}
      <PropertiesWithoutConciergeCard />
      {/* Feb 2026 · Anreise-Radar: nächste 3 Anreisen mit Adresse + Wetter.
          Auf Admin-Dashboard direkt unter Warnungen — Concierge sieht das
          Widget prominenter im vereinfachten ConciergeHome. */}
      <NextArrivalsRadar />
      {/* Feb 2026: hero card summing every open to-do across all customers.
          Click on the count toggles ?open=1 → filters the two lists below
          and propagates via link to /team/calendar. */}
      <OpenTodosCard onSummaryChange={setOpenSummary} />
      {/* Feb 2026 · 11 Sep 2026 · Cashflow + „Wer trägt wie viel"
          verschoben — sie stehen jetzt zusammen direkt oberhalb der
          „Selbstheilung des Buchungsstatus"-Karte, siehe unten. */}
      {showTour && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: `${withAlpha(BRAND_COLORS.navyDark, 0.75)}` }}
          data-testid="onboarding-tour"
        >
          <div className="bg-ivory max-w-xl w-full border border-gold/40 p-10">
            <div className="label-caps mb-3" style={{ color: BRAND_COLORS.gold }}>Willkommen im Team</div>
            <h2 className="font-display text-3xl text-navy mb-3">Kurze Rundtour</h2>
            <p className="text-[14px] text-navy-dark/70 mb-6 leading-relaxed">Drei Bereiche, mit denen Sie täglich arbeiten:</p>
            <ol className="space-y-4 text-[14px] text-navy-dark leading-relaxed mb-8">
              <li className="flex gap-4"><span className="font-display text-2xl leading-none pt-1 w-8" style={{ color: BRAND_COLORS.gold }}>01</span><div><strong className="text-navy">Kunden</strong> — <Link to="/team/customers" className="border-b border-gold">Kundenverwaltung</Link>: alle Eigentümer mit Anzahl Objekten und Buchungen.</div></li>
              <li className="flex gap-4"><span className="font-display text-2xl leading-none pt-1 w-8" style={{ color: BRAND_COLORS.gold }}>02</span><div><strong className="text-navy">Kalender</strong> — <Link to="/team/calendar" className="border-b border-gold">Termine</Link>: automatische Abwesenheiten plus manuelle Aufgaben und Ankünfte.</div></li>
              <li className="flex gap-4"><span className="font-display text-2xl leading-none pt-1 w-8" style={{ color: BRAND_COLORS.gold }}>03</span><div><strong className="text-navy">Wochenberichte</strong> — <Link to="/team/updates" className="border-b border-gold">Berichte senden</Link>: einmal pro Woche Foto + Zusammenfassung pro Objekt.</div></li>
            </ol>
            <button
              data-testid="onboarding-finish-btn"
              onClick={finishTour}
              className="bg-navy text-ivory px-8 py-3 text-[12px] tracking-[0.15em] uppercase hover:bg-navy-dark transition-colors"
              style={{ background: BRAND_COLORS.navy }}
            >
              Los geht's →
            </button>
          </div>
        </div>
      )}
      <div className="mb-12 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="label-caps mb-3">Weissblom Team</div>
          <h1 className="font-display text-4xl md:text-5xl text-navy">{t.team.dashboard_title}</h1>
          <p className="text-[15px] text-navy-dark/70 mt-3 max-w-2xl">{t.team.dashboard_lead}</p>
        </div>
        {stats && (
          <div
            className="bg-navy text-ivory px-5 py-4 md:px-6 md:py-5 flex flex-wrap items-stretch gap-x-6 gap-y-3 divide-x divide-ivory/15 shadow-sm relative"
            data-testid="team-cockpit"
            data-scope-concierge={activeConciergeId || ""}
            title="Aktueller Team-Status"
          >
            {/* Feb 2026 · Sichtbarer Scope-Hinweis, wenn der Admin gerade
                als konkreter Concierge Zahlen sieht — damit man niemals
                verwirrt ist, was gezählt wird. */}
            {user?.role === "admin" && activeConciergeName && (
              <div
                className="absolute -top-2 left-4 px-2 py-0.5 text-[9.5px] tracking-[0.15em] uppercase rounded-full flex items-center gap-1"
                style={{ background: BRAND_COLORS.gold, color: BRAND_COLORS.navy }}
                data-testid="team-cockpit-scope-hint"
              >
                Nur {activeConciergeName}
              </div>
            )}
            <Link to="/team/calendar" data-testid="cockpit-in-service" className="flex flex-col pr-6 first:pl-0 group">
              <span className="text-[10px] tracking-[0.2em] uppercase text-ivory/60">In Betreuung</span>
              <span className="font-display text-2xl md:text-3xl leading-none mt-1 flex items-baseline gap-2">
                {stats.in_service ?? 0}
                <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: STATUS_COLORS.blue }} />
              </span>
              <span className="text-[10.5px] text-ivory/50 mt-1 group-hover:text-gold transition-colors">Kunden aktuell abwesend</span>
            </Link>
            <Link to="/team/requests" data-testid="cockpit-requested" className="flex flex-col px-6 group">
              <span className="text-[10px] tracking-[0.2em] uppercase text-ivory/60">Angefragt</span>
              <span className="font-display text-2xl md:text-3xl leading-none mt-1 flex items-baseline gap-2">
                {stats.requested_bookings ?? 0}
                <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: BRAND_COLORS.gold }} />
              </span>
              <span className="text-[10.5px] text-ivory/50 mt-1 group-hover:text-gold transition-colors">Warten auf Bestätigung</span>
            </Link>
            <Link to="/team/requests" data-testid="cockpit-completed-today" className="flex flex-col pl-6 group">
              <span className="text-[10px] tracking-[0.2em] uppercase text-ivory/60">Erledigt heute</span>
              <span className="font-display text-2xl md:text-3xl leading-none mt-1 flex items-baseline gap-2">
                {stats.completed_today ?? 0}
                <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: STATUS_COLORS.green }} />
              </span>
              <span className="text-[10.5px] text-ivory/50 mt-1 group-hover:text-gold transition-colors">Aufgaben abgeschlossen</span>
            </Link>
          </div>
        )}
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          <Stat to="/team/customers" label={t.team.stats.customers} value={stats.customers} testId="stat-customers" />
          <Stat to="/team/properties" label={t.team.stats.properties} value={stats.properties} testId="stat-properties" />
          <Stat to="/team/calendar" label={t.team.stats.upcoming_absences} value={stats.upcoming_absences} testId="stat-absences" />
          <Stat to="/team/requests" label={t.team.stats.open_requests} value={stats.open_requests} testId="stat-requests" />
          <Stat to="/team/chat" label={t.team.stats.unread_messages} value={stats.unread_messages} testId="stat-unread" />
          <Link
            to="/team/updates"
            className="bg-white border border-navy/10 p-6 transition-colors hover:border-gold"
            style={draftCount > 0 ? { borderColor: "#C9A96180", background: withAlpha(BRAND_COLORS.gold, 0.06) } : {}}
            data-testid="stat-drafts"
          >
            <div className="label-caps mb-3" style={draftCount > 0 ? { color: BRAND_COLORS.goldDark } : {}}>
              Berichts-Entwürfe
            </div>
            <div className="font-display text-4xl md:text-5xl text-navy flex items-baseline gap-2">
              {draftCount === null ? "…" : draftCount}
              {draftCount > 0 && (
                <span className="text-[11px] tracking-[0.15em] uppercase" style={{ color: BRAND_COLORS.goldDark }}>
                  offen
                </span>
              )}
            </div>
          </Link>
        </div>
      )}

      {pending && (
        <div className="mb-8 border border-gold/50 bg-white p-6 md:p-7" data-testid="pending-updates-banner">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <div className="label-caps mb-2" style={{ color: BRAND_COLORS.gold }}>{t.updates.reminder_title}</div>
              <h2 className="font-display text-2xl text-navy">
                {pending.pending.length === 0
                  ? t.updates.reminder_all_done
                  : `${pending.pending.length} / ${pending.total_properties} Wochenberichte offen`}
              </h2>
              <p className="text-[13px] text-navy-dark/70 mt-1">{t.updates.reminder_lead}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                data-testid="send-monthly-digest-btn"
                onClick={async () => {
                  if (!(await confirmDialog({
                    title: "Monatszusammenfassung versenden?",
                    description: 'Alle Kunden mit E-Mail-Präferenz „monatlich" erhalten jetzt ihre Zusammenfassung.',
                    confirmText: "Senden",
                  }))) return;
                  try {
                    const { data } = await api.post("/team/send-monthly-digest");
                    toast.success(`Digest: ${data.sent} versendet · ${data.errors} Fehler · ${data.monthly_recipients} monatliche Empfänger`);
                  } catch (e) {
                    toast.error("Digest-Versand fehlgeschlagen.");
                  }
                }}
                className="text-[11px] tracking-[0.15em] uppercase text-navy-dark/70 hover:text-gold border-b border-gold/40 hover:border-gold pb-0.5 transition-colors"
              >
                Digest senden
              </button>
              <Link
                to="/team/updates"
                data-testid="pending-send-now-cta"
                className="border border-navy text-navy px-5 py-2.5 text-[12px] tracking-[0.15em] uppercase hover:bg-navy hover:text-ivory transition-colors"
              >
                {t.updates.reminder_send_now} →
              </Link>
            </div>
          </div>
          {pending.pending.length > 0 && (
            <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {pending.pending.map((p) => (
                <li key={p.property_id} className="flex items-center gap-3 pl-3 border-l-2 border-gold" data-testid={`pending-row-${p.property_id}`}>
                  <div className="min-w-0">
                    <div className="text-[13px] text-navy font-medium truncate">{p.property_name}</div>
                    <div className="text-[11px] text-navy-dark/60 truncate">{p.customer_name}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 gap-6">
        <QuoteDecisionHistoryCard />
        {/* Feb 2026 · 11 Sep 2026 · User-Directive: „Cashflow" +
            „Wer trägt wie viel" zusammen direkt oberhalb der
            „Selbstheilung des Buchungsstatus". */}
        {/* Feb 2026 · Umsatz-Auswertung (Cashflow-Balken der letzten 6 Monate). */}
        <RevenueChart />
        {/* Feb 2026 · Concierge-Property-Statistik: wer betreut wie viele
            Objekte, wo brennt es, welche Anreisen stehen an. Für Admins zeigt
            es alle Concierges, für Concierges nur die eigenen Zahlen. */}
        <ConciergeStatsCard />
        <StatusCleanupCard />
        <TelegramPushCard user={user} />
      </div>
    </div>
  );
}
